import { useEffect, useState } from "react";
import {
  ArrowRight,
  BedDouble,
  BookOpen,
  Camera,
  Check,
  Coins,
  EyeOff,
  Footprints,
  Gamepad2,
  Lock,
  LockKeyhole,
  MonitorPlay,
  Pause,
  Play,
  Pointer,
  QrCode,
  ScanFace,
  Sparkles,
  Sprout,
  Timer,
  ToggleRight,
  Volume2,
  VolumeX,
} from "lucide-react";
import { PLAN_DEFAULTS } from "@sprout/shared/plans";
import ShoeArt, { Sticker } from "../ShoeArt";
import { DEMO_STEPS } from "../ChoreDemo";
import { Overline, useDemo } from "../Layout";
import { Link, usePage } from "../router";
import { PORTAL_URL, ROUTES, SIGN_UP_URL } from "../links";
import { useReducedMotion } from "../motion";
import { FREE, PAID, limitValue, planPrice } from "../plan-format";

const LINES = [
  "Find your pair. Left shoe, right shoe!",
  "Slip them on, heel first. Wiggle your toes!",
  "Press the straps down flat, like a sticker.",
];
const BEAT_MS = 4200;
const canSpeak = () => typeof window !== "undefined" && "speechSynthesis" in window;

/** The hero sample: Buddy walks through three beats; "My turn" opens the full sample. */
function SampleCard() {
  const openDemo = useDemo();
  const reduced = useReducedMotion();
  const [step, setStep] = useState(1);
  const [playing, setPlaying] = useState(!reduced);
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => {
    if (!playing || reduced) return;
    const timer = window.setTimeout(() => setStep((s) => (s + 1) % 3), BEAT_MS);
    return () => window.clearTimeout(timer);
  }, [playing, reduced, step]);
  useEffect(() => () => void (canSpeak() && window.speechSynthesis.cancel()), []);
  const speak = () => {
    if (!canSpeak()) return;
    const synth = window.speechSynthesis;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    const line = new SpeechSynthesisUtterance(LINES[step]);
    line.rate = 0.95;
    line.onend = line.onerror = () => setSpeaking(false);
    synth.cancel();
    synth.speak(line);
    setSpeaking(true);
  };
  return (
    <div className="home-sample">
      <div className="home-sample-card">
        <div className="home-sample-top">
          <span>
            <Sprout size={16} /> Sample chore · Shoes on
          </span>
          <span>Step {step + 1} of 3</span>
        </div>
        <div className="home-sample-stage">
          <ShoeArt />
          <div className="home-buddy">
            <img src="/fox.svg" alt="Buddy the fox" />
            <span aria-live="polite">{LINES[step]}</span>
          </div>
        </div>
        <ol className="home-sample-steps">
          {DEMO_STEPS.map((label, i) => (
            <li key={label} className={i < step ? "done" : i === step ? "now" : ""}>
              <span className="bar">
                {i === step && (
                  <i
                    key={step + (playing ? "p" : "s")}
                    className={playing && !reduced ? "run" : "half"}
                    style={{ animationDuration: BEAT_MS + "ms" }}
                  />
                )}
              </span>
              <button type="button" onClick={() => (setStep(i), setPlaying(false))}>
                {i + 1} · {label}
              </button>
            </li>
          ))}
        </ol>
        <div className="home-sample-actions">
          {!reduced && (
            <button
              type="button"
              className="site-icon-btn big"
              aria-label={playing ? "Pause lesson" : "Play lesson"}
              onClick={() => setPlaying(!playing)}
            >
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
          )}
          {canSpeak() && (
            <button
              type="button"
              className="site-icon-btn big"
              aria-label={speaking ? "Stop Buddy" : "Hear Buddy"}
              aria-pressed={speaking}
              onClick={speak}
            >
              {speaking ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          )}
          <button type="button" className="site-btn primary grow" onClick={() => openDemo("try")}>
            My turn <ArrowRight size={17} />
          </button>
        </div>
      </div>
      <p className="home-sample-note">Try it here. No account, camera or family data.</p>
    </div>
  );
}

function Hero() {
  const openDemo = useDemo();
  return (
    <section className="home-hero site-wrap">
      <div className="home-intro">
        <Overline>Family routines</Overline>
        <h1>
          Less reminding.{" "}
          <br />
          More <em>“I did it.”</em>
        </h1>
        <p>
          Buddy shows your child how to do a chore on a shared screen. They try it, you cheer the
          effort, and points grow toward rewards you choose together.
        </p>
        <div className="home-hero-actions">
          <a className="site-btn primary large" href={SIGN_UP_URL}>
            Create a family <ArrowRight size={17} />
          </a>
          <button type="button" className="site-btn secondary large" onClick={() => openDemo()}>
            <Play size={16} /> Try a sample chore
          </button>
        </div>
        <ul className="home-ticks">
          <li>
            <Check size={15} /> Free for families
          </li>
          <li>
            <Check size={15} /> No card needed
          </li>
          <li>
            <Check size={15} /> No facial recognition
          </li>
        </ul>
      </div>
      <SampleCard />
    </section>
  );
}

function Steps() {
  return (
    <section id="how-it-works" className="home-steps site-section">
      <div className="site-wrap">
        <div className="home-steps-head">
          <div>
            <Overline>How it works</Overline>
            <h2 className="site-h2">Three small steps, every day.</h2>
          </div>
          <p>
            Approved effort becomes points toward rewards you create, like a movie night, a family
            dinner or an adventure afternoon.
          </p>
        </div>
        <div className="home-step-grid">
          <article className="home-step">
            <span className="home-step-num">1</span>
            <h3>You choose the challenge</h3>
            <p>Start from 15 everyday challenges or write your own. Pick photo, video or no proof for each.</p>
            <div className="home-step-demo linen" aria-hidden="true">
              <div className="home-task">
                <span className="lav">
                  <BedDouble size={15} />
                </span>
                <strong>Make your bed</strong>
                <small>Photo</small>
              </div>
              <div className="home-task">
                <span className="peach">
                  <Footprints size={15} />
                </span>
                <strong>Put on your shoes</strong>
                <small>No proof</small>
              </div>
              <div className="home-task">
                <span className="sky">
                  <BookOpen size={15} />
                </span>
                <strong>Read for 15 minutes</strong>
                <small>Video</small>
              </div>
            </div>
          </article>
          <article className="home-step">
            <span className="home-step-num">2</span>
            <h3>Buddy shows how</h3>
            <p>On the wall screen, your child taps their profile and watches a short 3D demo. Then it’s their turn.</p>
            <div className="home-step-demo cream" aria-hidden="true">
              <div className="home-quest">
                <span className="home-quest-sticker">
                  <Sticker kind="ready" size={38} />
                </span>
                <strong>Shoes on</strong>
                <span className="home-quest-meta">
                  <span>
                    <Coins size={16} /> +10
                  </span>
                  <span className="home-play">
                    <Play size={14} fill="currentColor" /> Play
                  </span>
                </span>
              </div>
            </div>
          </article>
          <article className="home-step">
            <span className="home-step-num">3</span>
            <h3>You cheer the effort</h3>
            <p>Approve from your phone and points land once. Or ask for another try with a kind note.</p>
            <div className="home-step-demo linen" aria-hidden="true">
              <div className="home-review">
                <div className="home-review-row">
                  <span className="home-review-thumb">
                    <Camera size={17} />
                  </span>
                  <span className="home-review-title">
                    <strong>Make your bed</strong>
                    <small>Leo · 7:42 AM</small>
                  </span>
                  <span className="home-pill">Waiting</span>
                </div>
                <div className="home-review-actions">
                  <span className="retry">Try again</span>
                  <span className="approve">
                    <Check size={14} /> Approve · +10
                  </span>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function WallApp() {
  return (
    <section id="wall-app" className="home-wall site-section">
      <div className="site-wrap home-wall-grid">
        <div className="home-tablet" aria-hidden="true">
          <div className="home-tablet-screen">
            <div className="home-tablet-top">
              <span>
                <img src="/fox.svg" alt="" />
                <strong>Leo</strong>
              </span>
              <span className="home-coins">
                <Coins size={16} /> 35
              </span>
            </div>
            <h3>Hi, Leo!</h3>
            <div className="home-tablet-garden">
              <div className="home-mound">
                <img src="/fox.svg" alt="" />
              </div>
            </div>
            <div className="home-tablet-quest">
              <div>
                <span className="home-quest-sticker lav">
                  <Sticker kind="bed" size={34} />
                </span>
                <strong>Make bed</strong>
                <span className="home-play">
                  <Play size={13} fill="currentColor" /> Play
                </span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <Overline>The wall app</Overline>
          <h2 className="site-h2">A shared screen that does the reminding.</h2>
          <p className="site-lead">
            Mount any tablet by the door and open Buddy Garden. It shows what’s next, keeps a garden
            growing, and keeps grown-up settings locked.
          </p>
          <div className="home-wall-points">
            <div>
              <span>
                <QrCode size={19} />
              </span>
              <div>
                <strong>Pairs in seconds</strong>
                <p>Scan the QR or type the 8-character code from your phone.</p>
              </div>
            </div>
            <div>
              <span>
                <Pointer size={19} />
              </span>
              <div>
                <strong>Kids tap their profile</strong>
                <p>No logins and no face scanning. Just their buddy.</p>
              </div>
            </div>
            <div>
              <span>
                <LockKeyhole size={19} />
              </span>
              <div>
                <strong>Lock it from anywhere</strong>
                <p>Lock, unlock or disconnect a named screen live.</p>
              </div>
            </div>
            <div>
              <span>
                <Gamepad2 size={19} />
              </span>
              <div>
                <strong>One round of play</strong>
                <p>A 15–60 second game or clip per try. Never endless.</p>
              </div>
            </div>
          </div>
          <a className="site-btn primary" href={PORTAL_URL}>
            <MonitorPlay size={18} /> Open the wall app
          </a>
        </div>
      </div>
    </section>
  );
}

function Coaching() {
  return (
    <section id="coaching" className="home-coach site-section">
      <div className="site-wrap home-coach-grid">
        <div>
          <Overline>Buddy’s coaching</Overline>
          <h2 className="site-h2">A kind word, right when they need it.</h2>
          <p className="site-lead">
            Every family gets guided coaching from Buddy. With {PAID.name} and your family’s AI switch
            on, Buddy can also give short encouragement written for each task.
          </p>
          <ul className="home-coach-list">
            <li>
              <span>
                <ToggleRight size={17} />
              </span>
              Off until a grown-up turns it on
            </li>
            <li>
              <span>
                <EyeOff size={17} />
              </span>
              Sees only the task and your child’s age, never photos or videos
            </li>
          </ul>
        </div>
        <div className="home-coach-card" aria-hidden="true">
          <div className="home-coach-task">
            <span className="home-quest-sticker">
              <Sticker kind="ready" size={30} />
            </span>
            <strong>Shoes on</strong>
            <small>Step 3 of 3</small>
          </div>
          <div className="home-coach-say">
            <img src="/fox.svg" alt="" />
            <div>
              <span>Tricky straps! Press the fuzzy side down flat, like a sticker.</span>
              <small>
                <Sparkles size={13} /> AI coaching · Plus
              </small>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Summaries() {
  const paid = planPrice(PAID);
  return (
    <section className="home-summaries site-wrap">
      <div className="home-summary">
        <Overline>Privacy &amp; safety</Overline>
        <h2>Made for kids. Private by default.</h2>
        <ul className="home-summary-list">
          <li>
            <ScanFace size={18} /> No facial recognition
          </li>
          <li>
            <Lock size={18} /> Media is never public
          </li>
          <li>
            <Sparkles size={18} /> No photos or videos to AI
          </li>
          <li>
            <Timer size={18} /> Proof deleted after {PLAN_DEFAULTS.retentionDays} days
          </li>
        </ul>
        <Link className="site-arrow-link" href={ROUTES.privacy}>
          How we handle proof <ArrowRight size={16} />
        </Link>
      </div>
      <div className="home-summary linen">
        <Overline>Pricing</Overline>
        <h2>Free to start. Plus when you’re ready.</h2>
        <div className="home-mini-plans">
          <div>
            <span>{FREE.name}</span>
            <strong>{planPrice(FREE).amount}</strong>
            <small>
              {FREE.limits.wallDevices} screens · {limitValue("proofStorageMb", FREE.limits.proofStorageMb)} of proof
            </small>
          </div>
          <div className="paid">
            <span>{PAID.name}</span>
            <strong>
              {paid.amount}
              <em>{paid.per}</em>
            </strong>
            <small>
              {PAID.limits.wallDevices} screens · {limitValue("proofStorageMb", PAID.limits.proofStorageMb)} · AI coaching
            </small>
          </div>
        </div>
        <Link className="site-arrow-link" href={ROUTES.pricing}>
          Compare plans <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

function Garden() {
  return (
    <section className="site-wrap">
      <div className="home-garden">
        <div>
          <h2>
            Little efforts,
            <br />a growing garden.
          </h2>
          <p>Set up your family in a couple of minutes, then pair the wall screen from your phone.</p>
          <div className="home-garden-actions">
            <a className="site-btn primary large" href={SIGN_UP_URL}>
              Create a family <ArrowRight size={17} />
            </a>
            <Link className="site-plain-link" href={ROUTES.faq}>
              Questions? Read the FAQ
            </Link>
          </div>
        </div>
        <img src="/growth-garden.svg" alt="" />
      </div>
    </section>
  );
}

export default function Home() {
  usePage("Sprout · Family routines");
  return (
    <>
      <Hero />
      <Steps />
      <WallApp />
      <Coaching />
      <Summaries />
      <Garden />
    </>
  );
}
